/**
 * Whose turn is it on an offer?
 *
 * Two entry points share one rule:
 *
 * - isOfferInMyCourt(offer, uid, fields): list form, used by the Trading
 *   badge (navbar, bottom tab, Trading hub) to count every open offer that is
 *   waiting on THIS user (owner, 2026-11-02: a counter puts the offer back in
 *   the original sender's court, so sent offers count too once countered).
 * - isOfferAwaitingMyResponse(offer, uid, { routeId, ...fields }): the
 *   single-offer form used only to decide whether to show the native "turn on
 *   notifications" pop-up when someone opens an offer they received
 *   (2026-10-07). It adds the route check so stale state from the previous
 *   offer after a route change can't count.
 *
 * Both are deliberately stricter than the pages' own button logic and never
 * guess: anything missing or malformed means no.
 *
 * Yes only when ALL hold:
 * - status is 'pending' or 'countered'
 * - the user is one of the two participants (and they are different people)
 * - the history has at least one 'offer' or 'response' entry, and the LAST
 *   such entry came from the OTHER participant. Notes are ignored: a comment
 *   doesn't change whose turn it is (same rule as OfferPage's canRespond).
 *
 * @param {object|null} offer   { id, status, history, [initiatorField], [recipientField] }
 * @param {string|null} uid     signed-in user
 * @param {object} p
 * @param {string} p.initiatorField  'fromUserId' (trade) | 'initiatorId' (purchase)
 * @param {string} p.recipientField  'toUserId' (trade) | 'responderId' (purchase)
 */
export function isOfferInMyCourt(offer, uid, { initiatorField, recipientField }) {
  if (!offer || typeof offer !== 'object') return false;
  if (typeof uid !== 'string' || !uid) return false;
  if (offer.status !== 'pending' && offer.status !== 'countered') return false;

  const a = offer[initiatorField];
  const b = offer[recipientField];
  if (typeof a !== 'string' || typeof b !== 'string' || !a || !b || a === b) return false;
  if (uid !== a && uid !== b) return false;

  if (!Array.isArray(offer.history)) return false;
  const actions = offer.history.filter((e) => e && (e.type === 'offer' || e.type === 'response'));
  if (actions.length === 0) return false;
  const last = actions[actions.length - 1];
  if (typeof last.from !== 'string' || !last.from) return false;

  const other = uid === a ? b : a;
  return last.from === other;
}

export function isOfferAwaitingMyResponse(offer, uid, { routeId, initiatorField, recipientField }) {
  if (!offer || typeof offer !== 'object') return false;
  if (typeof routeId !== 'string' || !routeId || offer.id !== routeId) return false;
  return isOfferInMyCourt(offer, uid, { initiatorField, recipientField });
}

export const TRADE_OFFER_FIELDS = Object.freeze({ initiatorField: 'fromUserId', recipientField: 'toUserId' });
export const PURCHASE_OFFER_FIELDS = Object.freeze({ initiatorField: 'initiatorId', recipientField: 'responderId' });

// The 48-hour clock that functions/expirePendingOffers.js enforces: a counter
// restarts it, so a countered offer is measured from its last update, a
// pending one from creation. The scheduled expiry runs on an interval, so an
// offer can sit past the cutoff with status still 'pending' for a while; the
// badge applies the same cutoff itself rather than nag about an offer the
// page is about to show as expired (TradingHub already filters the same way).
export const OFFER_RESPONSE_WINDOW_MS = 48 * 60 * 60 * 1000;

function toMillis(ts) {
  if (!ts) return NaN;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  const n = new Date(ts).getTime();
  return Number.isFinite(n) ? n : NaN;
}

export function isOfferResponseWindowOpen(offer, nowMs = Date.now()) {
  if (!offer || typeof offer !== 'object') return false;
  const clock = offer.status === 'countered'
    ? (offer.offerUpdatedAt || offer.updatedAt || offer.createdAt)
    : offer.createdAt;
  const start = toMillis(clock);
  // No usable timestamp: never guess that it is still open. Every real offer
  // carries createdAt, so this only hides malformed docs.
  if (!Number.isFinite(start)) return false;
  return nowMs - start < OFFER_RESPONSE_WINDOW_MS;
}

/**
 * Count the open offers waiting on `uid`. `offers` is a list of
 * { kind: 'trade' | 'purchase', ...docData } (the hook dedupes by id first).
 */
export function countOffersAwaitingResponse(offers, uid, nowMs = Date.now()) {
  if (!Array.isArray(offers)) return 0;
  let n = 0;
  for (const offer of offers) {
    const fields = offer?.kind === 'purchase' ? PURCHASE_OFFER_FIELDS : TRADE_OFFER_FIELDS;
    if (isOfferInMyCourt(offer, uid, fields) && isOfferResponseWindowOpen(offer, nowMs)) n += 1;
  }
  return n;
}
